import { DateTime } from "luxon";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useWeekIndex } from "@/contexts/WeekIndexProvider";
import { useWeekViewContext } from "@/contexts/WeekView";
import { useDepartmentFlags } from "@/hooks/useDepartmentFlags";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { useShownWeek } from "@/hooks/useShownWeek";
import { trackEvent } from "@/utils/analytics";
import { getVisibleWeekOffset, getWeekStart } from "@/utils/dateUtils";
import { DEFAULT_WEEK_START_DAY } from "@/utils/models";
import { staffingWeeklyPath } from "@/utils/staffingRoutes";
import { StaffingDateTypeSelector } from "../staffing/StaffingDateTypeSelector";
import { formatDateRange } from "./formatDateRange";
import { useArrowKeys } from "./useArrowKeys";

const DAYS_PER_WEEK = 7;
const ISO_DATE = "yyyy-MM-dd";

/**
 * The Week view's date control: the arrows, Today and the date picker move the shown week (`useShownWeek`) by date,
 * from the schedule's first through last day: with the schedule cache on the whole schedule, off the loaded weeks.
 */
export const WeekDateControl: FC = () => {
	const { i18n } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	// Only hospital demos (Hospital type + inhouse2).
	const { isHospitalView: isHospitalDemoView } = useDepartmentFlags();
	const { viewStartDay } = useWeekIndex();
	const { weekStart, goToWeek } = useShownWeek();
	const { firstDate, lastDate } = useWeekViewContext();
	const isDynamicWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.StaffingDynamicWeekStart);
	const isRTL = i18n.dir() === "rtl";
	const location = useLocation();
	const navigate = useNavigate();
	const isOnStaffingPage = location.pathname.includes("/staffing") && !location.pathname.includes("/staffing/monthly");
	const weekEnd = weekStart?.plus({ days: DAYS_PER_WEEK - 1 });

	const moveToWeek = useCallback(
		(target: DateTime, eventName: string) => {
			if (!weekStart || !goToWeek(target)) return;
			const newWeekStart = getWeekStart(target, DEFAULT_WEEK_START_DAY);
			// `history.replaceState` instead of `navigate({ replace: true })` avoids a full RouterProvider
			// re-render (~300ms) for a cosmetic URL change.
			if (isOnStaffingPage) window.history.replaceState(null, "", staffingWeeklyPath(chosenDepartment.department_id, newWeekStart.toFormat(ISO_DATE)));
			trackEvent(eventName, {
				previous_week_start_date: weekStart.toFormat(ISO_DATE),
				week_start_date: newWeekStart.toFormat(ISO_DATE),
				week_end_date: newWeekStart.plus({ days: DAYS_PER_WEEK - 1 }).toFormat(ISO_DATE),
			});
		},
		[weekStart, goToWeek, isOnStaffingPage, chosenDepartment.department_id],
	);

	const handlePrevClick = useCallback(() => {
		if (!weekStart || !firstDate || weekStart <= firstDate.startOf("day")) return;
		moveToWeek(weekStart.minus({ weeks: 1 }), "previous_week_clicked");
	}, [weekStart, firstDate, moveToWeek]);

	const handleNextClick = useCallback(() => {
		if (!weekEnd || !lastDate || weekEnd >= lastDate.startOf("day")) return;
		moveToWeek(weekEnd.plus({ days: 1 }), "next_week_clicked");
	}, [weekEnd, lastDate, moveToWeek]);

	useArrowKeys(handlePrevClick, handleNextClick);

	const handleResetWeek = useCallback(() => {
		const now = DateTime.now().setZone(chosenDepartment.timezone);
		const offset = isDynamicWeekStartEnabled ? getVisibleWeekOffset(viewStartDay) : 0;
		// Before the viewer's start day, today is on screen in the week that began the Sunday before.
		const target = now.weekdayISO % DAYS_PER_WEEK < offset ? now.minus({ weeks: 1 }) : now;
		if (!goToWeek(target)) return;
		const newWeekStart = getWeekStart(target, DEFAULT_WEEK_START_DAY).toFormat(ISO_DATE);
		// The URL names the week a refresh opens, so it follows Today as it follows the arrows.
		if (isOnStaffingPage) window.history.replaceState(null, "", staffingWeeklyPath(chosenDepartment.department_id, newWeekStart));
		trackEvent("reset_week_index", { current_date: now.toFormat(ISO_DATE), week_start_date: newWeekStart });
	}, [chosenDepartment.timezone, chosenDepartment.department_id, isDynamicWeekStartEnabled, viewStartDay, goToWeek, isOnStaffingPage]);

	const handleDateSelected = useCallback(
		(selectedDate: DateTime) => {
			if (!goToWeek(selectedDate)) return;
			if (isOnStaffingPage) {
				navigate(staffingWeeklyPath(chosenDepartment.department_id, getWeekStart(selectedDate, DEFAULT_WEEK_START_DAY).toFormat(ISO_DATE)));
			}
			trackEvent("weekly_date_picker_date_selected", { selected_date: selectedDate.toFormat(ISO_DATE) });
		},
		[goToWeek, isOnStaffingPage, navigate, chosenDepartment.department_id],
	);

	const formattedDateRange = useMemo(
		() => formatDateRange(weekStart, weekEnd, i18n.language, isHospitalDemoView),
		[weekStart, weekEnd, i18n.language, isHospitalDemoView],
	);

	return (
		<StaffingDateTypeSelector
			// The weekly Staffing route's date control, which is what the walkthrough step describes. The Day Schedule
			// and monthly renders of the same selector deliberately carry no anchor.
			data-annie-target="staffing-date-range"
			currentDateText={formattedDateRange}
			initialPageType={"WEEKLY"}
			onNextClick={isRTL ? handlePrevClick : handleNextClick}
			onPreviousClick={isRTL ? handleNextClick : handlePrevClick}
			onResetButtonClick={handleResetWeek}
			currentDate={weekStart}
			onDateSelected={handleDateSelected}
			minDate={firstDate}
			maxDate={lastDate}
		/>
	);
};
